import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Lock, Shield, Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_INFO = {
  admin:       { label: 'Administrateur', color: 'bg-forest text-white',         desc: 'Accès complet : lecture, modification et gestion des utilisateurs.' },
  contributor: { label: 'Contributeur',   color: 'bg-terracotta text-white',      desc: 'Peut ajouter et modifier des données généalogiques.' },
  reader:      { label: 'Lecture seule',  color: 'bg-stone-200 text-stone-700',   desc: 'Consultation uniquement, aucune modification possible.' },
};

export const ProfilePage = () => {
  const { user, login } = useAuth();
  const navigate        = useNavigate();

  const [form, setForm]       = useState({
    nom:             user?.nom     || '',
    prenom:          user?.prenom  || '',
    currentPassword: '',
    newPassword:     '',
    confirmPassword: '',
  });
  const [saving,   setSaving]   = useState(false);
  const [message,  setMessage]  = useState(null);  // {type:'success'|'error', text:''}

  const role     = user?.role || 'reader';
  const roleInfo = ROLE_INFO[role] || ROLE_INFO.reader;

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setMessage({ type: 'error', text: 'Les nouveaux mots de passe ne correspondent pas.' });
      return;
    }

    setSaving(true);
    try {
      const payload = { nom: form.nom, prenom: form.prenom };
      if (form.newPassword) {
        payload.current_password = form.currentPassword;
        payload.new_password     = form.newPassword;
      }
      await axios.patch(`${API_URL}/api/auth/me`, payload, { withCredentials: true });
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès.' });
      setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Erreur lors de la mise à jour.' });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="profile-page" className="space-y-6 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-terracotta hover:text-terracotta-light -ml-2">
        <ArrowLeft size={18} className="mr-2" /> Retour
      </Button>

      <div>
        <h1 className="text-4xl font-bold text-forest mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Mon profil
        </h1>
        <p className="text-stone-500">Gérez vos informations personnelles</p>
      </div>

      {/* ── Carte identité + rôle ── */}
      <div className="bg-white border border-parchment-dark p-6 flex items-center gap-6">
        <div className="w-16 h-16 bg-stone-100 border border-parchment-dark flex items-center justify-center text-forest font-bold text-2xl shrink-0">
          {user.prenom?.charAt(0)?.toUpperCase() || <User size={28} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-semibold text-stone-900">{user.prenom} {user.nom}</p>
          <p className="text-sm text-stone-500 flex items-center gap-1 mt-0.5">
            <Mail size={13} /> {user.email}
          </p>
          <p className="text-xs text-stone-400 mt-0.5">Login : {user.login}</p>
        </div>
        <div className="shrink-0 text-right">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${roleInfo.color}`}>
            <Shield size={12} /> {roleInfo.label}
          </span>
          <p className="text-xs text-stone-400 mt-2 max-w-[160px] text-right leading-tight hidden md:block">
            {roleInfo.desc}
          </p>
        </div>
      </div>

      {/* ── Description du rôle (mobile) ── */}
      <div className="md:hidden bg-stone-50 border border-parchment-dark px-4 py-3 text-sm text-stone-600 flex items-start gap-2">
        <Shield size={15} className="text-forest mt-0.5 shrink-0" />
        {roleInfo.desc}
      </div>

      {/* ── Formulaire modification ── */}
      <form onSubmit={handleSave} className="bg-white border border-parchment-dark p-6 space-y-6">
        <h2 className="text-xl font-semibold text-forest" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Modifier mes informations
        </h2>

        {message && (
          <div className={`px-4 py-3 text-sm border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Nom / Prénom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="prenom" className="text-stone-700 font-medium mb-1 block">Prénom</Label>
            <Input id="prenom" name="prenom" value={form.prenom} onChange={handleChange}
              className="border-parchment-dark focus:ring-forest" />
          </div>
          <div>
            <Label htmlFor="nom" className="text-stone-700 font-medium mb-1 block">Nom</Label>
            <Input id="nom" name="nom" value={form.nom} onChange={handleChange}
              className="border-parchment-dark focus:ring-forest" />
          </div>
        </div>

        {/* Changement de mot de passe */}
        <div className="border-t border-parchment-dark pt-6 space-y-4">
          <div className="flex items-center gap-2 text-stone-600 text-sm font-medium">
            <Lock size={15} /> Changer le mot de passe <span className="font-normal text-stone-400">(facultatif)</span>
          </div>
          <div>
            <Label htmlFor="currentPassword" className="text-stone-700 font-medium mb-1 block">Mot de passe actuel</Label>
            <Input id="currentPassword" name="currentPassword" type="password" value={form.currentPassword}
              onChange={handleChange} className="border-parchment-dark focus:ring-forest"
              autoComplete="current-password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="newPassword" className="text-stone-700 font-medium mb-1 block">Nouveau mot de passe</Label>
              <Input id="newPassword" name="newPassword" type="password" value={form.newPassword}
                onChange={handleChange} className="border-parchment-dark focus:ring-forest"
                autoComplete="new-password" />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-stone-700 font-medium mb-1 block">Confirmer</Label>
              <Input id="confirmPassword" name="confirmPassword" type="password" value={form.confirmPassword}
                onChange={handleChange} className="border-parchment-dark focus:ring-forest"
                autoComplete="new-password" />
            </div>
          </div>
        </div>

        <Button type="submit" disabled={saving}
          className="w-full bg-forest text-white hover:bg-forest-light py-5 font-semibold flex items-center justify-center gap-2">
          <Save size={17} />
          {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
        </Button>
      </form>
    </div>
  );
};